class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.config = {
      autorotate: this.dataset.autorotate === 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed) * 1000,
      moveTime: parseFloat(this.dataset.speed),
      space: 100,
    };

    this.currentSlide = 0;
    this.isTransitioning = false;
    this.autoplayTimer = null;

    if (theme.config.mqlSmall) {
      this.initOnMobile();
    } else {
      this.initOnDesktop();
    }

    document.addEventListener('matchSmall', () => {
      this.unload();
      this.initOnMobile();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.initOnDesktop();
    });
  }

  initOnDesktop() {
    switch(this.dataset.layout) {
      case 'carousel':
        this.initCarousel();
        break;
      case 'marquee':
        this.initMarquee();
        break;
      default:
        this.initMarqueeOptimized();
    }
  }

  initOnMobile() {
    switch(this.dataset.mobileLayout) {
      case 'carousel':
        this.initCarousel();
        break;
      case 'marquee':
        this.initMarquee();
        break;
    }
  }

  unload() {
    // Stop autoplay
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    // Remove button listeners
    this.removeNextPrevListeners();

    // Clean up marquee
    if (this.classList.contains('marquee')) {
      this.classList.remove('marquee');
      this.querySelectorAll('.announcement-slider[aria-hidden]').forEach((slider) => {
        slider.remove();
      });
    }

    // Clean up observer
    if (this.observer && typeof this.observer.unobserve === 'function') {
      this.observer.unobserve(this);
    }

    // Reset carousel state
    this.currentSlide = 0;
    this.isTransitioning = false;
  }

  initCarousel() {
    if (this.dataset.blockCount == 1) {
      return;
    }

    const slider = this.querySelector('.announcement-slider');
    const slides = this.querySelectorAll('.announcement-slider__slide');
    
    if (!slider || slides.length === 0) {
      return;
    }

    // Setup slider container
    slider.style.display = 'flex';
    slider.style.transition = 'transform 0.5s ease-in-out';
    slider.style.width = '100%';
    
    // Setup slides
    slides.forEach((slide, index) => {
      slide.style.minWidth = '100%';
      slide.style.display = 'flex';
      if (index !== 0) {
        slide.style.opacity = '0';
      }
    });

    // Show first slide
    this.showSlide(0);

    // Setup navigation buttons
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.onNextPrevClick = this.handleNextPrevClick.bind(this);
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onNextPrevClick);
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onNextPrevClick);
    }

    // Setup autoplay
    if (this.config.autorotate) {
      this.startAutoplay();
    }

    // Touch support for mobile
    this.addTouchSupport(slider, slides);
  }

  showSlide(index) {
    const slides = this.querySelectorAll('.announcement-slider__slide');
    const totalSlides = slides.length;

    if (totalSlides === 0) return;

    // Wrap around
    if (index >= totalSlides) {
      index = 0;
    } else if (index < 0) {
      index = totalSlides - 1;
    }

    // Hide all slides
    slides.forEach(slide => {
      slide.style.opacity = '0';
      slide.style.position = 'absolute';
      slide.style.visibility = 'hidden';
    });

    // Show current slide
    slides[index].style.opacity = '1';
    slides[index].style.position = 'relative';
    slides[index].style.visibility = 'visible';

    this.currentSlide = index;
  }

  nextSlide() {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    this.showSlide(this.currentSlide + 1);
    
    setTimeout(() => {
      this.isTransitioning = false;
    }, 500);
  }

  previousSlide() {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    this.showSlide(this.currentSlide - 1);
    
    setTimeout(() => {
      this.isTransitioning = false;
    }, 500);
  }

  startAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
    }
    
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.config.autorotateSpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  addTouchSupport(slider, slides) {
    let touchStartX = 0;
    let touchEndX = 0;

    slider.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      this.stopAutoplay();
    }, { passive: true });

    slider.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
      
      if (this.config.autorotate) {
        this.startAutoplay();
      }
    }, { passive: true });

    const handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swiped left - next slide
          this.nextSlide();
        } else {
          // Swiped right - previous slide
          this.previousSlide();
        }
      }
    };

    this.handleSwipe = handleSwipe;
  }

  removeNextPrevListeners() {
    if (this.prevButton && this.onNextPrevClick) {
      this.prevButton.removeEventListener('click', this.onNextPrevClick);
    }
    if (this.nextButton && this.onNextPrevClick) {
      this.nextButton.removeEventListener('click', this.onNextPrevClick);
    }
  }

  handleNextPrevClick(event) {
    event.preventDefault();
    const target = event.currentTarget;
    
    // Stop autoplay temporarily
    if (this.config.autorotate) {
      this.stopAutoplay();
      setTimeout(() => {
        this.startAutoplay();
      }, 3000); // Resume after 3 seconds
    }
    
    if (target.name === 'next') {
      this.nextSlide();
    } else if (target.name === 'previous') {
      this.previousSlide();
    }
  }

  initMarqueeOptimized() {
    setTimeout(() => {
      this.marqueeAnimation();
    });

    // Pause when out of view
    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isAnimated && this.classList.remove('marquee--paused');
        } else {
          this.isAnimated && this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);

    window.addEventListener('resize', this.marqueeAnimation.bind(this));
  }

  marqueeAnimation() {
    const slider = this.querySelector('.announcement-slider');

    if (slider.scrollWidth > this.clientWidth) {
      if (!this.isAnimated) {
        for (let index = 0; index < 2; index++) {
          const clone = slider.cloneNode(true);
          clone.setAttribute('aria-hidden', true);
          this.appendChild(clone);
        }
        
        this.classList.add('marquee');
        this.style.setProperty('--duration', `${(slider.scrollWidth / this.config.space) * this.config.moveTime}s`);
        this.isAnimated = true;
      }
    } else if (this.isAnimated) {
      for (let index = 0; index < 2; index++) {
        this.removeChild(this.lastElementChild);
      }
      this.classList.remove('marquee');
      this.isAnimated = false;
    }
  }

  initMarquee() {
    setTimeout(() => {
      const slider = this.querySelector('.announcement-slider');

      for (let index = 0; index < 10; index++) {
        const clone = slider.cloneNode(true);
        clone.setAttribute('aria-hidden', true);
        this.appendChild(clone);
      }
  
      this.classList.add('marquee');
      this.style.setProperty('--duration', `${(slider.clientWidth / this.config.space) * this.config.moveTime}s`);
    });

    // Pause when out of view
    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.classList.remove('marquee--paused');
        } else {
          this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);
  }
}

customElements.define('announcement-bar', AnnouncementBar);
